import {
  CLIENT_SNAPSHOT_INTERVAL_MS,
  type PathPoint,
  type PawnSnapshot,
  PawnState,
  type SkillSnapshot,
  TILE_SIZE,
} from '@dreamgame/shared';
import { computed, signal } from '@preact/signals';
import { clearQueueForPawn, syncPawnState } from './commandQueue';

// Re-export PathPoint for convenience
export type { PathPoint };

// Interpolation data for smooth movement (client-only)
export interface InterpolationData {
  readonly prevX: number;
  readonly prevY: number;
  readonly targetX: number;
  readonly targetY: number;
  readonly startTime: number;
}

// PawnData extends PawnSnapshot with client-only fields
// This is the client's representation of a pawn entity
export interface PawnData extends Omit<PawnSnapshot, 'entityType' | 'path' | 'skills'> {
  // Mutable path array for client updates
  readonly path: ReadonlyArray<PathPoint> | null;
  // Mutable skills array
  readonly skills: ReadonlyArray<SkillSnapshot>;
  // Client-only: interpolation data for smooth rendering
  readonly interpolation: InterpolationData | null;
  // Combat target network ID (0 = no target)
  readonly combatTarget: number;
  // Combat state (0 = none, 1 = approaching, 2 = attacking, etc.)
  readonly combatState: number;
}

// State - using mutable Map with version counter for performance
const pawnsMap = new Map<number, PawnData>();
// Track previous pawn state for queue sync (IDLE -> active transitions)
const previousStateMap = new Map<number, number>();
export const pawnsVersion = signal(0);
export const playerId = signal<number>(0);

// Accessor for the pawns map (read-only access)
export const pawns = {
  get value(): Map<number, PawnData> {
    return pawnsMap;
  },
};

// Increment version to notify dependents of changes
const notifyPawnsChanged = (): void => {
  pawnsVersion.value = pawnsVersion.value + 1;
};

// ============================================================================
// PERFORMANCE: Memoized Derived State
// Uses version counter instead of Map reference for cache invalidation
// ============================================================================

// Cache for allPawns
let _allPawnsCache: PawnData[] = [];
let _allPawnsCacheVersion = -1;

export const allPawns = computed(() => {
  const version = pawnsVersion.value;
  if (version !== _allPawnsCacheVersion) {
    _allPawnsCacheVersion = version;
    _allPawnsCache = Array.from(pawnsMap.values());
  }
  return _allPawnsCache;
});

// Cache for ownedPawns
let _ownedPawnsCache: PawnData[] = [];
let _ownedPawnsCacheVersion = -1;
let _ownedPawnsCachePlayerId = 0;

export const ownedPawns = computed(() => {
  const version = pawnsVersion.value;
  const currentPlayerId = playerId.value;
  if (version !== _ownedPawnsCacheVersion || currentPlayerId !== _ownedPawnsCachePlayerId) {
    _ownedPawnsCacheVersion = version;
    _ownedPawnsCachePlayerId = currentPlayerId;
    _ownedPawnsCache = allPawns.value.filter((p) => p.ownerId === currentPlayerId);
  }
  return _ownedPawnsCache;
});

// CombatState enum values (must match shared/src/combat/types.ts)
const COMBAT_STATE_APPROACHING = 1;

// Check if an entity is being targeted for attack by selected pawns in approaching state
export const isApproachingTarget = ({
  networkId,
  selectedPawnIds,
}: {
  networkId: number;
  selectedPawnIds: ReadonlySet<number>;
}): boolean => {
  for (const pawn of ownedPawns.value) {
    // Only check selected pawns
    if (!selectedPawnIds.has(pawn.networkId)) {
      continue;
    }
    // Only show indicator when approaching (not when fighting)
    if (pawn.combatTarget === networkId && pawn.combatState === COMBAT_STATE_APPROACHING) {
      return true;
    }
  }
  return false;
};

// Actions
export const setPlayerId = ({ id }: { id: number }): void => {
  playerId.value = id;
};

export const addPawn = ({ pawn }: { pawn: PawnData }): void => {
  pawnsMap.set(pawn.networkId, pawn);
  notifyPawnsChanged();
};

export const removePawn = ({ networkId }: { networkId: number }): void => {
  pawnsMap.delete(networkId);
  previousStateMap.delete(networkId);
  clearQueueForPawn({ pawnNetworkId: networkId });
  notifyPawnsChanged();
};

// Fields that UI components care about (not just rendering)
const UI_RELEVANT_FIELDS = new Set([
  'health',
  'maxHealth',
  'state',
  'name',
  'ownerId',
  'isDowned',
  'combatTarget',
  'combatState',
  'task',
  'hunger',
  'rest',
  'mood',
  'skills',
]);

// Sync command queue when pawn state changes (extracted to reduce complexity)
const trackPawnStateForQueue = ({
  networkId,
  newState,
}: {
  networkId: number;
  newState: number;
}): void => {
  const prevState = previousStateMap.get(networkId) ?? PawnState.IDLE;
  previousStateMap.set(networkId, newState);
  if (prevState !== newState) {
    syncPawnState({ pawnNetworkId: networkId, prevState, newState });
  }
};

// Build interpolation data for smooth position transitions (extracted to reduce complexity)
const buildInterpolation = ({
  pawn,
  updates,
}: {
  pawn: PawnData;
  updates: Partial<PawnData>;
}): InterpolationData | null => {
  const hasPositionChange =
    updates.x !== undefined &&
    updates.y !== undefined &&
    (updates.x !== pawn.x || updates.y !== pawn.y);

  if (!hasPositionChange) {
    return pawn.interpolation;
  }

  const t = pawn.interpolation ? getInterpolationT({ startTime: pawn.interpolation.startTime }) : 0;
  return {
    prevX: pawn.interpolation
      ? lerp({ a: pawn.interpolation.prevX, b: pawn.interpolation.targetX, t })
      : pawn.x,
    prevY: pawn.interpolation
      ? lerp({ a: pawn.interpolation.prevY, b: pawn.interpolation.targetY, t })
      : pawn.y,
    targetX: updates.x as number,
    targetY: updates.y as number,
    startTime: performance.now(),
  };
};

// PERFORMANCE: Check if updates contain UI-relevant fields using for...in (no Object.keys allocation)
const hasUIRelevantChange = ({ updates }: { updates: Partial<PawnData> }): boolean => {
  for (const key in updates) {
    if (UI_RELEVANT_FIELDS.has(key)) {
      return true;
    }
  }
  return false;
};

export const updatePawn = ({
  networkId,
  updates,
}: {
  networkId: number;
  updates: Partial<PawnData>;
}): void => {
  const pawn = pawnsMap.get(networkId);
  if (!pawn) {
    return;
  }

  if (updates.state !== undefined) {
    trackPawnStateForQueue({ networkId, newState: updates.state });
  }

  const interpolation = buildInterpolation({ pawn, updates });

  // Check if hunger specifically changed (for debugging)
  const hungerChanged =
    updates.hunger !== undefined && Math.abs((pawn.hunger ?? 0) - updates.hunger) > 0.1;
  if (hungerChanged) {
    console.log(
      `[entities] Pawn ${networkId} hunger changed: ${pawn.hunger?.toFixed(1)} -> ${updates.hunger?.toFixed(1)}, notifying UI`
    );
  }

  // PERFORMANCE: Mutate in-place instead of spread allocation
  Object.assign(pawn, updates);
  (pawn as { interpolation: InterpolationData | null }).interpolation = interpolation;

  if (hungerChanged || hasUIRelevantChange({ updates })) {
    notifyPawnsChanged();
  }
};

// Linear interpolation helper
const lerp = ({ a, b, t }: { a: number; b: number; t: number }): number => {
  return a + (b - a) * t;
};

// Smooth step easing (ease in-out) for more natural movement
const smoothStep = ({ t }: { t: number }): number => {
  return t * t * (3 - 2 * t);
};

// Get interpolation progress (0 to 1, clamped)
const getInterpolationT = ({ startTime }: { startTime: number }): number => {
  const elapsed = performance.now() - startTime;
  const linear = Math.min(1, elapsed / CLIENT_SNAPSHOT_INTERVAL_MS);
  // Apply smoothstep for more natural movement
  return smoothStep({ t: linear });
};

// PERFORMANCE: Preallocated result object to avoid allocations per pawn per frame
const _interpolatedPosResult = { x: 0, y: 0 };

// PERFORMANCE: Returns preallocated object - caller must use values immediately
// Do not store the returned reference as it will be mutated on next call
export const getInterpolatedPosition = ({
  pawn,
}: {
  pawn: PawnData;
}): Readonly<{ x: number; y: number }> => {
  if (!pawn.interpolation) {
    _interpolatedPosResult.x = pawn.x;
    _interpolatedPosResult.y = pawn.y;
    return _interpolatedPosResult;
  }

  const t = getInterpolationT({ startTime: pawn.interpolation.startTime });

  _interpolatedPosResult.x = lerp({
    a: pawn.interpolation.prevX,
    b: pawn.interpolation.targetX,
    t,
  });
  _interpolatedPosResult.y = lerp({
    a: pawn.interpolation.prevY,
    b: pawn.interpolation.targetY,
    t,
  });
  return _interpolatedPosResult;
};

export const getPawn = ({ networkId }: { networkId: number }): PawnData | undefined => {
  return pawns.value.get(networkId);
};

// Click radius in tiles - allows clicking near pawns to select them
const PAWN_CLICK_RADIUS = 0.75;
const PAWN_CLICK_RADIUS_SQ = PAWN_CLICK_RADIUS * PAWN_CLICK_RADIUS;

export const getPawnAtPosition = ({
  worldX,
  worldY,
}: {
  worldX: number;
  worldY: number;
}): PawnData | null => {
  // Convert world coordinates to tile units for distance calculation
  const clickTileX = worldX / TILE_SIZE;
  const clickTileY = worldY / TILE_SIZE;

  let closestPawn: PawnData | null = null;
  let closestDistSq = PAWN_CLICK_RADIUS_SQ;

  for (const pawn of pawns.value.values()) {
    // Calculate distance from click to pawn center (pawn position + 0.5)
    const dx = pawn.x + 0.5 - clickTileX;
    const dy = pawn.y + 0.5 - clickTileY;
    const distSq = dx * dx + dy * dy;

    if (distSq < closestDistSq) {
      closestDistSq = distSq;
      closestPawn = pawn;
    }
  }

  return closestPawn;
};

export const clearEntities = (): void => {
  pawnsMap.clear();
  previousStateMap.clear();
  notifyPawnsChanged();
  playerId.value = 0;
};
